import { CutlistState, MaterialGroup, PartItem } from '@cutr/constants/cutlist';
import {
  SMALL_PART_EDGEBANDING_LIMITS,
  SMALL_PARTS_EDGEBANDING_FLAG,
} from '@cutr/constants/small-part-edgebanding';
import {
  type NestingOutput,
  type PartFitErrorData,
  partsFitSheet,
} from '@cutr/nesting';
import * as Sentry from '@sentry/react';
import { useFlag } from '@unleash/proxy-client-react';
import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useLeadDetails } from '@/api/account';
import { api } from '@/api/backend';
import { useNestingErrorStore, usePartsErrorStore } from '@/api/errors';
import { useAgentLoggedIn, useAuthStore } from '@/api/login';
import { getMaterial } from '@/api/materials';
import {
  getGroupNestingFields,
  getPartNestingFields,
  hashGroupForNesting,
  useActiveGroupPricingSensitiveData,
  useMaterialGroups,
  useMaterialGroupState,
  useSaveSensitiveData,
  useSheetEdgeTrimConfig,
} from '@/api/materialsGroup';
import {
  SheetGroup,
  useNestingStatusStore,
  useNestingStore,
} from '@/api/nesting';
import { usePricingStore } from '@/api/pricing';
import {
  isValidBeamPart,
  useCutlistParts,
  useCutlistState,
  useSavingDraftStore,
} from '@/api/store';
import Card from '@/blocks/Card';
import { InfoMessage } from '@/blocks/InfoMessage';
import { Pagination } from '@/blocks/Pagination';
import { useIsAutoNestingRunning } from '@/hooks';
import { useGetPriceForMaterialGroups } from '@/queries/agent';
import { useUpdateCutlist } from '@/queries/crud';
import { getSourceConfig } from '@/theme';
import { createBackendGrouping } from '@/utils/backend-compatibility';
import { useDebounce } from '@/utils/hooks';
import { isInIframe, isTruthy } from '@/utils/misc';
import {
  convertGroupToInput,
  groupPartsForNesting,
  NestingGroup,
} from '@/utils/nesting';
import { NestingOrchestrator } from '@/utils/nestingOrchestrator';
import {
  hasManualEdgebanding,
  needsPresaw,
} from '@/utils/smallPartEdgebanding';

import styles from './Nesting.module.css';
import { RenderSheet } from './NestingRenderer';
const ENVIRONMENT = import.meta.env.VITE_CUTR_ENV;
const selectHasMaterials = (state: CutlistState) => state.hasMaterials;

function detectGroupChanges(
  currentNestingGroups: Record<string, NestingGroup>,
  debouncedParts: PartItem[],
  previousHashes: Record<string, number>,
  getPartFields: (p: PartItem) => object
): {
  currentHashes: Record<string, number>;
  changedGroupIds: string[];
  removedGroupIds: string[];
} {
  const currentHashes: Record<string, number> = {};
  const changedGroupIds: string[] = [];
  const removedGroupIds: string[] = [];

  Object.keys(currentNestingGroups).forEach((id) => {
    const nestingGroup = currentNestingGroups[id];
    const groupParts = debouncedParts.filter((p) => p.groupId === id);
    const currentHash = hashGroupForNesting(
      nestingGroup.materialGroup,
      groupParts,
      getPartFields
    );
    currentHashes[id] = currentHash;

    const previousHash = previousHashes[id];
    if (previousHash === undefined || previousHash !== currentHash) {
      changedGroupIds.push(id);
    }
  });

  Object.keys(previousHashes).forEach((id) => {
    if (!currentHashes[id]) {
      removedGroupIds.push(id);
    }
  });

  return { currentHashes, changedGroupIds, removedGroupIds };
}

function allPartsFit(nestingGroup: NestingGroup) {
  const { sheetConfig, parts } = convertGroupToInput(nestingGroup);
  try {
    partsFitSheet(parts, sheetConfig);
    return true;
  } catch {
    return false;
  }
}

export function useRunNesting(
  getNestingFields: (p: PartItem) => object = getPartNestingFields
) {
  const { t } = useTranslation();
  const isSmallPartEdgebandingEnabled = useFlag(SMALL_PARTS_EDGEBANDING_FLAG);
  const smallPartEdgebandingLimits = isSmallPartEdgebandingEnabled
    ? SMALL_PART_EDGEBANDING_LIMITS
    : undefined;
  const getPartFields = React.useCallback(
    (p: PartItem) =>
      isSmallPartEdgebandingEnabled
        ? { ...getNestingFields(p), smallPartEdgebanding: true }
        : getNestingFields(p),
    [getNestingFields, isSmallPartEdgebandingEnabled]
  );
  const { addError: addNestingError, resetErrors } = useNestingErrorStore();
  const { nestingConfig } = getSourceConfig();
  const { addPartError, resetPartsNestingError } = usePartsErrorStore();
  const { setNesting } = useNestingStore.getState();
  const hasMaterials = useCutlistState(selectHasMaterials);
  const parts = useCutlistParts();
  const groups = useMaterialGroups();
  const debouncedParts = useDebounce(parts, 500);
  const sheetEdgeTrimConfig = useSheetEdgeTrimConfig();
  const { setIsSaving } = useSavingDraftStore();
  const { id: cutlistId } = useParams();

  const groupsRef = React.useMemo(
    () =>
      JSON.stringify(
        groups.map((group) => {
          const groupParts = debouncedParts
            .filter((p) => p.groupId === group.id)
            .map(getPartFields);
          return {
            groupParts,
            ...getGroupNestingFields(group),
          };
        })
      ),
    [debouncedParts, groups, getPartFields]
  );

  const nestingGroupRef = React.useRef<Record<string, NestingGroup>>({});
  const nestingOutputRef = React.useRef<Record<string, NestingOutput>>({});
  const groupHashesRef = React.useRef<Record<string, number>>({});
  const abortControllersRef = React.useRef<
    Record<MaterialGroup['id'], { abort: () => void }>
  >({});
  const orchestratorRef = React.useRef<ReturnType<
    typeof NestingOrchestrator
  > | null>(null);

  const updateNesting = React.useCallback(() => {
    const sheetGroups = Object.keys(nestingOutputRef.current)
      .map((id) => {
        const nestingOutput = nestingOutputRef.current[id];
        const nestingGroup = nestingGroupRef.current[id];

        if (!nestingGroup || !nestingGroup.materialGroup) return;

        return { ...nestingOutput, ...nestingGroup } as SheetGroup;
      })
      .filter(isTruthy);
    setNesting(sheetGroups);
  }, [setNesting]);

  const handleGroupOutputUpdate = React.useCallback(
    (groupId: string) => (output: NestingOutput) => {
      nestingOutputRef.current[groupId] = output;
      updateNesting();
    },
    [updateNesting]
  );

  const handleNestingError = React.useCallback(
    (message: string, data?: PartFitErrorData) => {
      if (!data) {
        addNestingError(message);
        Sentry.captureException(new Error(message));
        return;
      }

      const errorMessage = t('cutlist-summary.nesting.partFitError', {
        name: data.name,
        partSize: [data.partLengthMM, data.partWidthMM].join('x'),
        sheetSize: [data.sheetLengthMM, data.sheetWidthMM].join('x'),
      });

      const fullMessage = data.hint
        ? errorMessage +
          ' ' +
          t(`cutlist-summary.nesting.partFitErrorHints.${data.hint}`)
        : errorMessage;

      addPartError(data?.id, 'nesting', fullMessage);
    },
    [addNestingError, addPartError, t]
  );

  const runNesting = React.useCallback(() => {
    // On a hard refresh we can render before materials have finished loading.
    // If we "run" nesting too early we will compute/store hashes and then later
    // (once materials are available) we may incorrectly think nothing changed,
    // causing nesting to never run. Guard until materials are cached.
    if (!hasMaterials) {
      return;
    }

    resetErrors();

    const currentNestingGroups = Object.fromEntries(
      Object.entries(groupPartsForNesting(debouncedParts, groups)).map(
        ([id, nestingGroup]) => [
          id,
          { ...nestingGroup, smallPartEdgebandingLimits },
        ]
      )
    );
    nestingGroupRef.current = currentNestingGroups;

    const nestedPartIds = new Set(
      Object.values(currentNestingGroups).flatMap((g) =>
        g.parts.map((p) => p.id)
      )
    );
    resetPartsNestingError(
      usePartsErrorStore
        .getState()
        .partErrors.map((e) => e.partItemId)
        .filter((id) => !nestedPartIds.has(id))
    );

    if (!Object.keys(currentNestingGroups).length) {
      nestingOutputRef.current = {};
      groupHashesRef.current = {};
      Object.values(abortControllersRef.current).forEach(({ abort }) =>
        abort()
      );
      abortControllersRef.current = {};
      updateNesting();
      setIsSaving(false);
      return;
    }

    // On first run, seed refs from stored nesting outputs so we can skip
    // re-running nesting for groups that haven't changed since last save.
    if (Object.keys(groupHashesRef.current).length === 0) {
      const { initialNestingOutputs } = useNestingStore.getState();
      if (Object.keys(initialNestingOutputs).length > 0) {
        Object.keys(currentNestingGroups).forEach((groupId) => {
          const storedOutput = initialNestingOutputs[groupId];
          if (!storedOutput) return;
          nestingOutputRef.current[groupId] = storedOutput;
          const nestingGroup = currentNestingGroups[groupId];
          if (!allPartsFit(nestingGroup)) return;
          const groupParts = debouncedParts.filter(
            (p) => p.groupId === groupId
          );
          groupHashesRef.current[groupId] = hashGroupForNesting(
            nestingGroup.materialGroup,
            groupParts,
            getPartFields
          );
        });

        if (Object.keys(nestingOutputRef.current).length > 0) {
          updateNesting();
        }
      }
    }

    const { currentHashes, changedGroupIds, removedGroupIds } =
      detectGroupChanges(
        currentNestingGroups,
        debouncedParts,
        groupHashesRef.current,
        getPartFields
      );

    removedGroupIds.forEach((id) => {
      delete nestingOutputRef.current[id];
      delete groupHashesRef.current[id];
      if (abortControllersRef.current[id]) {
        abortControllersRef.current[id].abort();
        delete abortControllersRef.current[id];
        useNestingStatusStore.getState().removeProgress(id);
      }
    });

    if (removedGroupIds.length > 0) {
      updateNesting();
    }

    groupHashesRef.current = currentHashes;

    if (changedGroupIds.length === 0) {
      setIsSaving(false);
      return;
    }

    if (!orchestratorRef.current) {
      if (!cutlistId) return;
      orchestratorRef.current = NestingOrchestrator({
        nestingConfig,
        cutlistId,
      });
    }
    const orchestrator = orchestratorRef.current;

    setIsSaving(true);

    changedGroupIds.forEach((groupId) => {
      const nestingGroup = currentNestingGroups[groupId];
      if (!nestingGroup) return;

      orchestrator.runGroup(groupId, nestingGroup, {
        beforeRun: () => {
          useNestingStatusStore.getState().resetGroupProgress(groupId);
          resetPartsNestingError(nestingGroup.parts.map((p) => p.id));
        },
        onWorkerStart: (groupId, workerIndex, sheetSize) => {
          useNestingStatusStore
            .getState()
            .setWorkerMeta(groupId, workerIndex, sheetSize);
        },
        onOutput: (id, output, articleCode) => {
          if (
            articleCode &&
            nestingGroup.materialGroup.sheetSizeSelection === 'automatic' &&
            nestingGroup.materialGroup.core1 !== articleCode
          ) {
            const currentGroup = useMaterialGroupState
              .getState()
              .groups.find((g) => g.id === nestingGroup.materialGroup.id);
            if (currentGroup) {
              useMaterialGroupState.getState().setGroup({
                ...currentGroup,
                core1: articleCode,
              });
            }
          }
          handleGroupOutputUpdate(id)(output);
        },
        onProgress: (groupId, workerIndex, totalWorkers, progress) => {
          useNestingStatusStore
            .getState()
            .setWorkerProgress(groupId, workerIndex, totalWorkers, progress);
        },
        onError: handleNestingError,
      });

      abortControllersRef.current[groupId] = {
        abort: () => orchestrator.abortGroup(groupId),
      };
    });

    updateNesting();
  }, [
    cutlistId,
    debouncedParts,
    groups,
    getPartFields,
    handleGroupOutputUpdate,
    handleNestingError,
    hasMaterials,
    nestingConfig,
    resetErrors,
    resetPartsNestingError,
    setIsSaving,
    smallPartEdgebandingLimits,
    updateNesting,
  ]);

  React.useEffect(() => {
    runNesting();
  }, [groupsRef, hasMaterials, sheetEdgeTrimConfig, runNesting]);
}

export const NestingHooks = () => {
  useRunNesting();
  usePricingFromNesting();
  return null;
};

const useActiveSheetGroups = () => {
  const sheetGroups = useNestingStore((state) => state.sheetGroups);
  const activeTab = useMaterialGroupState((state) => state.activeGroup);
  return sheetGroups.filter((group) =>
    group.parts.find((p) => !activeTab || p.groupId === activeTab)
  );
};

export const SmallPartEdgebandingNestingNotices = ({
  parts,
}: {
  parts: PartItem[];
}) => {
  const { t } = useTranslation();
  const isEnabled = useFlag(SMALL_PARTS_EDGEBANDING_FLAG);
  if (!isEnabled) return null;

  return (
    <>
      {parts.some(needsPresaw) && (
        <InfoMessage
          variant="warning"
          message={t('cutlist-summary.nesting.smallPartEdgebanding.presaw')}
        />
      )}
      {parts.some(hasManualEdgebanding) && (
        <InfoMessage
          variant="warning"
          message={t('cutlist-summary.nesting.smallPartEdgebanding.manual')}
        />
      )}
    </>
  );
};

export const Nesting = () => {
  const sheetGroups = useActiveSheetGroups();
  const { t } = useTranslation();
  const activeGroups = useMaterialGroupState();

  const getSheetDimensions = (group: SheetGroup) => {
    const { sheetConfig, materialGroup, sheets } = group;

    if (materialGroup.sheetSizeSelection === 'automatic') {
      return materialGroup.automaticSheetSizeMaterials.map((code) => {
        const material = getMaterial(code);
        if (!material) return {};
        const isSelected =
          material.lengthMM === sheetConfig.lengthMM &&
          material.widthMM === sheetConfig.widthMM;
        const dimensionString = `${material.lengthMM} x ${material.widthMM}`;
        return {
          dimensionString: isSelected
            ? `${dimensionString} (${t(`cutlist-summary.nesting.sheetCount`, {
                count: sheets.length,
              })})`
            : dimensionString,
          key: `${material.lengthMM}x${material.widthMM} ${material.articleCode}`,
          isSelected,
        };
      });
    }

    return [
      {
        dimensionString: `${sheetConfig.lengthMM} x ${sheetConfig.widthMM}`,
        key: `${sheetConfig.lengthMM}x${sheetConfig.widthMM}`,
        isSelected: true,
      },
    ];
  };

  return (
    <Card>
      <h3>{t('cutlist-summary.nesting.title')}</h3>
      <p />

      <div>
        {sheetGroups.map((group, id) => {
          const thickness = group.sheetConfig.thicknessUM / 1000 + 'mm';
          const size: [number, number] = [
            group.sheetConfig.lengthMM,
            group.sheetConfig.widthMM,
          ];
          const sheetStackingType = activeGroups.groups.find(
            (g) => g.id === group.materialGroup.id
          )?.sheetStackingType;
          const sheetDimensions = getSheetDimensions(group);

          return (
            <div key={id}>
              <div id={group.materialGroup.id}>
                <div className="opposites">
                  <div className={styles.info}>
                    <label>
                      {t(`cutlist-summary.nesting.partInfo.thickness`)}
                    </label>
                    <div>{thickness}</div>
                  </div>
                  {ENVIRONMENT !== 'production' &&
                    group.sheetConfig.sheetEdgeTrimConfig && (
                      <div className={styles.info}>
                        <label>{'Margin'}</label>
                        <div>
                          {`↓: ${group.sheetConfig.sheetEdgeTrimConfig?.trimThickness.length1TrimThicknessMM}
                      ↑: ${group.sheetConfig.sheetEdgeTrimConfig?.trimThickness.length2TrimThicknessMM}
                      ←: ${group.sheetConfig.sheetEdgeTrimConfig?.trimThickness.width1TrimThicknessMM}
                      →: ${group.sheetConfig.sheetEdgeTrimConfig?.trimThickness.width2TrimThicknessMM}`}
                        </div>
                      </div>
                    )}
                  <div className={styles.info}>
                    <label>{t(`cutlist-summary.nesting.partInfo.size`)}</label>
                    {sheetDimensions.map(
                      ({ dimensionString, isSelected, key }) => (
                        <div
                          key={key}
                          className={cn(
                            styles.info,
                            isSelected
                              ? styles.selectedDimension
                              : styles.notSelectedDimension
                          )}
                        >
                          {dimensionString}
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>

              {(!sheetStackingType || sheetStackingType === 'disabled') && (
                <Pagination navKey="sheet">
                  {group.sheets.map((sheet, idx) => (
                    <div key={idx}>
                      <RenderSheet
                        size={size}
                        sheet={sheet}
                        sheetEdgeTrimConfig={
                          group.sheetConfig.sheetEdgeTrimConfig
                        }
                        wrapperWidth="auto"
                      />
                    </div>
                  ))}
                </Pagination>
              )}

              {sheetStackingType === 'enabled' && (
                <Pagination navKey="packet">
                  {group?.packets?.map((packet, idx) => {
                    const sheet = group.sheets.find((sheet) =>
                      packet.sheetIds.includes(String(sheet.sheetId))
                    );

                    return (
                      <div key={idx}>
                        <RenderSheet
                          size={size}
                          sheet={sheet}
                          sheetEdgeTrimConfig={
                            group.sheetConfig.sheetEdgeTrimConfig
                          }
                          wrapperWidth="auto"
                        />
                      </div>
                    );
                  })}
                </Pagination>
              )}
            </div>
          );
        })}
      </div>

      <SmallPartEdgebandingNestingNotices
        parts={sheetGroups.flatMap((group) => group.parts)}
      />
    </Card>
  );
};

let beamsRef = '';
let sheetsRef = '';
function usePricingFromNesting() {
  const pricing = usePricingStore();
  const { clientNumber } = useAuthStore();
  const saveSensitiveData = useSaveSensitiveData();
  const { mutateAsync } = useUpdateCutlist();
  const priceSensitiveData = useActiveGroupPricingSensitiveData();
  const { groups } = useMaterialGroupState();
  const store = useCutlistState();
  const isAgentLoggedIn = useAgentLoggedIn();
  const { id } = useParams();
  const userLead = useLeadDetails();
  const { mutateAsync: getPriceForMaterialGroups } =
    useGetPriceForMaterialGroups(id!);
  const sheetGroups = useNestingStore((state) => state.sheetGroups);
  const debouncedSheets = useDebounce<SheetGroup[]>(sheetGroups, 500);
  const isAutoNestingRunning = useIsAutoNestingRunning();

  const allParts = useCutlistParts();
  const beamParts = allParts
    .filter(isValidBeamPart)
    .filter((part) => part.partType === 'beam');
  const debouncedBeams = useDebounce<PartItem[]>(beamParts, 500);

  sheetsRef = JSON.stringify(debouncedSheets);
  beamsRef = JSON.stringify(debouncedBeams);

  React.useEffect(() => {
    if (!store.orderId || !id) return;
    if (isAutoNestingRunning) return;

    if (!debouncedSheets.length && !debouncedBeams.length) {
      pricing.setError(null);
      pricing.setAmountVat(0);
      pricing.setAmountNoVat(0);
      pricing.setPricing([]);
      pricing.toggleManualQuote(false);

      return;
    }

    const cutlistGroups = createBackendGrouping(debouncedSheets, groups);

    if (isAgentLoggedIn && !userLead.email) {
      return;
    }

    if (isAgentLoggedIn && userLead.email) {
      getPriceForMaterialGroups({
        ...priceSensitiveData,
        id,
        materialGroups: cutlistGroups,
      })
        .then((data) => {
          pricing.setError(null);
          pricing.setAmountVat(data.totalAmountInclVAT);
          pricing.setAmountNoVat(data.totalAmountExclVAT);
          pricing.setPricing(data.pricingLineItems);
          pricing.toggleManualQuote(data.manualQuote);
        })
        .catch((e) => {
          pricing.setError(e);
        });
      return;
    }

    if (!isInIframe()) {
      mutateAsync({})
        .then((data) => {
          pricing.setError(null);
          pricing.setAmountVat(data.totalAmountInclVAT);
          pricing.setAmountNoVat(data.totalAmountExclVAT);
          pricing.setPricing(data.pricingLineItems);
          pricing.toggleManualQuote(data.manualQuote);
        })
        .catch((e) => {
          pricing.setError(e);
        });
      return;
    }

    api
      .pricing(cutlistGroups, clientNumber || '')
      .then((data) => {
        pricing.setError(null);
        pricing.setAmountVat(data.totalAmountInclVAT);
        pricing.setAmountNoVat(data.totalAmountExclVAT);
        pricing.setPricing(data.pricingLineItems);
        pricing.toggleManualQuote(data.manualQuote);
      })
      .catch((e) => {
        pricing.setError(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sheetsRef,
    beamsRef,
    saveSensitiveData,
    priceSensitiveData,
    userLead.email,
    isAutoNestingRunning,
  ]);
}
